import {dataTestId} from '@hconnect/uikit'
import {TextField, TextFieldProps} from '@mui/material'
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment'
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider'
import {TimePicker} from '@mui/x-date-pickers/TimePicker'
import moment from 'moment'
import {Control, Controller, FieldValues, Path} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

export interface ControlledTimePickerFieldProps<T extends FieldValues> {
  name: Path<T>
  control: Control<T>
  label: string
  testId: string
  disabled?: boolean
}

const isValidTimeFormat = (value: string): boolean => {
  if (!value) return true
  return moment(value, 'HH:mm', true).isValid()
}

export const ControlledTimePickerField = <T extends FieldValues>({
  name,
  control,
  label,
  testId,
  disabled = false
}: ControlledTimePickerFieldProps<T>): JSX.Element => {
  const {t} = useTranslation()

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Controller
        name={name}
        control={control}
        rules={{
          required: !disabled && t('error.startTimeRequired'),
          validate: (value) => {
            if (disabled || !value) return true
            return isValidTimeFormat(value) || t('error.invalidProductionTimeFormat')
          }
        }}
        render={({field: {value, onChange}, fieldState: {error}}) => {
          return (
            <TimePicker
              label={label}
              value={value ? moment(value, 'HH:mm') : null}
              onChange={(newValue: moment.Moment | null) => {
                onChange(newValue ? newValue.format('HH:mm') : '')
              }}
              disabled={disabled}
              ampm={false}
              views={['hours', 'minutes']}
              inputFormat="HH:mm"
              renderInput={(params: TextFieldProps) => (
                <TextField
                  {...params}
                  label={label}
                  disabled={disabled}
                  {...dataTestId(testId)}
                  error={!!error}
                  helperText={error?.message || ''}
                />
              )}
            />
          )
        }}
      />
    </LocalizationProvider>
  )
}
